import React from 'react';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  ComposedChart,
} from 'recharts';
import { cn } from '../../lib/utils';
import { borderPrimary, cardClass, largeTextSize, primaryText, regularText, semiBoldText, smallTextSize } from '../../styling/stylingHelper';
import { useAppSettingsStore } from '../../stores/appSettingsStore';

export interface GraphSeries {
  label: string;
  values: number[];
  color: string;
}

export interface HighlightData {
  x: string;
  values: Record<string, number>;
}

interface GraphCardProps {
  title: string;
  xLabels: string[];
  series: GraphSeries[];
  highlight?: HighlightData;
  currency?: string;
  height?: number;
  className?: string;
  /** Hide the built-in top-right legend (e.g. when the parent renders its own). */
  hideLegend?: boolean;
  /** Custom content rendered on the right of the header (e.g. a selector + legend). Replaces the built-in legend. */
  headerRight?: React.ReactNode;
}

// Smart number formatting function with decimals param
const formatNumber = (
  value: number,
  currency?: string,
  decimals: number = 1
): string => {
  const absValue = Math.abs(value);
  let formatted = '';

  if (absValue >= 1000000) {
    formatted = `${(value / 1000000).toFixed(decimals)}M`;
  } else if (absValue >= 1000) {
    formatted = `${(value / 1000).toFixed(decimals)}k`;
  } else {
    formatted = value.toFixed(decimals);
  }

  return currency ? `${currency}${formatted}` : formatted;
};

// Smart tick formatter that prevents duplicate labels
const formatTickSmart = (
  value: number,
  allTicks: number[],
  currency: string = '€'
): string => {
  const hasThousandOrMore = allTicks.some(v => Math.abs(v) >= 1000);

  // If any tick is ≥1000, switch EVERYONE to "k" notation with 1 decimal where it makes sense
  if (hasThousandOrMore) {
    const absValue = Math.abs(value);

    if (absValue < 1000) {
      // Below 1000 → show normal with 0 decimals (keeps chart clean)
      return `${currency}${absValue.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
    }

    // ≥1000 → show in thousands with exactly 1 decimal (e.g. 1256 → 1.3k)
    const inThousands = absValue / 1000;
    const formatted = inThousands.toFixed(1).replace(/\.0$/, ''); // removes .0 → 1k instead of 1.0k if whole

    // Optional: you can force .0 if you prefer consistency: remove the .replace above
    return `${currency}${formatted}k`;
  }

  // No value ≥1000 → just format normally with 0 decimals
  return `${currency}${Math.abs(value).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
};


// Tooltip values, legend, etc. use 1 decimal (except for numbers < 1000, which also uses 1)
const formatTooltip = (value: number, currency?: string) => {
  return formatNumber(value, currency, 1);
};

function getEightNiceTicks(min: number, max: number): number[] {
  // Always 8 ticks, including min and max.
  if (min === max) {
    // For edge case: only one value
    if (min === 0) {
      // All data zero
      return [0, 0.125, 0.25, 0.375, 0.5, 0.625, 0.75, 0.875, 1];
    } else {
      // All values same but not 0
      const up = min * 1.2;
      return [min, min + (up - min) * 0.125, min + (up - min) * 0.25, min + (up - min) * 0.375, min + (up - min) * 0.5, min + (up - min) * 0.625, min + (up - min) * 0.75, min + (up - min) * 0.875, up];
    }
  }
  const step = (max - min) / 7;
  return Array.from({ length: 8 }, (_, i) => min + step * i);
}

// Utility to measure the width of a text using a canvas context
const measureTextWidth = (text: string, font: string): number => {
  if (typeof window === "undefined") return 60; // default for SSR or non-browser
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return 60;
  ctx.font = font;
  return ctx.measureText(text).width;
};

export const GraphCard: React.FC<GraphCardProps> = ({
  title,
  xLabels,
  series,
  highlight,
  currency = '€',
  height = 400,
  className,
  hideLegend = false,
  headerRight,
}) => {
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const darkMode = useAppSettingsStore((s) => s.darkMode);

  // Transform series data into recharts format
  const data = xLabels.map((label, index) => ({
    name: label,
    ...series.reduce((acc, s) => {
      acc[s.label] = s.values[index];
      return acc;
    }, {} as Record<string, number>),
  }));

  // Calculate Y axis domain: always start at 0, go 20% higher than the highest value across all series
  let maxY = 0;
  series.forEach((s) => {
    s.values.forEach((v) => {
      if (typeof v === 'number' && !isNaN(v)) {
        if (v > maxY) maxY = v;
      }
    });
  });

  // Find the minY value, checking all series, all numbers; always clamp to 0 or minimum found (to allow for negative, if ever).
  let minY = 0;
  let foundMin = false;
  series.forEach((s) => {
    s.values.forEach((v) => {
      if (typeof v === 'number' && !isNaN(v)) {
        if (!foundMin || v < minY) {
          minY = v;
          foundMin = true;
        }
      }
    });
  });
  // Clamp to 0 for min if all values >= 0 (classic charts)
  minY = minY < 0 ? minY : 0;

  // max with headroom
  const yMax = maxY * 1.2;
  const yMin = minY; // could be negative if data goes negative, else 0

  // Generate 6 ticks
  const yTicks = getEightNiceTicks(yMin, yMax === 0 ? 1 : yMax);

  // Set domain from ticks
  const yDomain: [number, number] = [yTicks[0], yTicks[7]];

  // --------- DYNAMIC Y-AXIS WIDTH LOGIC BEGIN -----------
  // Compute max y label, and measure its width with correct font, to dynamically size left axis area
  const fontSize = 13; // match YAxis tick fontSize
  const fontFamily = 'Inter, ui-sans-serif, system-ui, sans-serif'; // or update as in your CSS
  // Compose a font string for canvas measurement
  const fontString = `400 ${fontSize}px ${fontFamily}`;
  // Get all y labels (formatted with smart logic to prevent duplicates)
  const yLabels = yTicks.map((v) => formatTickSmart(v, yTicks, currency));
  // Find the label with the max width
  let maxWidth = 0;
  yLabels.forEach(label => {
    const w = measureTextWidth(label, fontString);
    if (w > maxWidth) {
      maxWidth = w;
    }
  });

  // Add some padding (e.g. label + ~24px: 12px left + 12px right)
  // You may adjust padding for alignment as needed
  const axisWidth = Math.ceil(maxWidth + 24); 

  // --------- DYNAMIC Y-AXIS WIDTH LOGIC END -------------

  // Custom legend
  const renderLegend = () => {
    return (
      <div className="flex justify-end items-center gap-4">
        {series.map((s) => (
          <div key={s.label} className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-[4px]" 
              style={{ backgroundColor: s.color }}
            />
            <span className={cn(smallTextSize, regularText, primaryText)}>{s.label}</span>
          </div>
        ))}
      </div>
    );
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // Deduplicate and prefer entries with stroke color
      const uniquePayload: any[] = [];
      payload.forEach((entry: any) => {
        const existing = uniquePayload.find((item: any) => item.dataKey === entry.dataKey);
        if (!existing) {
          uniquePayload.push(entry);
        } else if (!existing.stroke || existing.stroke === 'none') {
          // Replace colorless entry (e.g., from <Area>) with one that has stroke (e.g., from <Line>)
          const idx = uniquePayload.indexOf(existing);
          uniquePayload[idx] = entry;
        }
      });

      return (
        <div className={cn("border", borderPrimary, cardClass, "p-3")}>
          <p className={cn(smallTextSize, semiBoldText, primaryText, "mb-2")}>{label}</p>
          {uniquePayload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-2 mb-1 last:mb-0">
              {/* Only the legend swatch is colored; the name and value stay neutral/black. */}
              <span
                className="w-2.5 h-2.5 rounded-[3px] flex-shrink-0"
                style={{ backgroundColor: entry.stroke && entry.stroke !== 'none' ? entry.stroke : (entry.color || '#000') }}
              />
              <span className={cn(smallTextSize, regularText, primaryText)}>{entry.dataKey}</span>
              <span className={cn(smallTextSize, semiBoldText, primaryText, "ml-auto pl-4")}>
                {formatTooltip(entry.value, currency)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };
  // Simplified custom cursor: 10px wide semi-transparent grey rectangle from the first series line down to the x-axis
  const CustomCursor = (props: any) => {
    const { points, height: chartHeight, yAxis, payload } = props;

    if (!points || points.length === 0 || !payload || payload.length === 0) return null;

    let yPos: number | null = null;
    const x = points[0].x;

    // Use first payload entry (corresponding to first series)
    const firstPayload = payload[0];
    if (firstPayload && 'value' in firstPayload) {
      if (yAxis && typeof yAxis.scale === 'function') {
        yPos = yAxis.scale(firstPayload.value);
      } else if (points[0].y !== undefined) {
        yPos = points[0].y;
      }
    }

    if (yPos === null || typeof yPos !== 'number') return null;

    return (
      <rect
        x={x - 5}
        y={yPos}
        width={10}
        height={chartHeight - yPos}
        fill={darkMode ? '#1E3A4E' : '#E5E7EB'}
        opacity={0.3}
      />
    );
  };

  // Custom X-axis tick with active styling
  const CustomXAxisTick = (props: any) => {
    const { x, y, payload, index } = props;
    const isActive = activeIndex === index;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fill={isActive ? '#7047EB' : (darkMode ? '#9CA3AF' : '#6b7280')}
          fontSize={13}
          fontWeight={isActive ? 600 : 400}
        >
          {payload.value}
        </text>
      </g>
    );
  };

  return (
    <div className={cn('w-full', cardClass, className)}>
      <div className="flex justify-between items-start gap-4 p-4">
        <h3 className={cn(primaryText, largeTextSize, semiBoldText)}>{title}</h3>
        {headerRight ? headerRight : (hideLegend ? null : renderLegend())}
      </div>
      <div className="pb-2 pr-2">
        <ResponsiveContainer width="100%" height={height}>
          <ComposedChart 
            data={data} 
            margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
            onMouseMove={(state: any) => {
              if (state && state.activeTooltipIndex !== undefined) {
                setActiveIndex(state.activeTooltipIndex);
              }
            }}
            onMouseLeave={() => {
              setActiveIndex(null);
            }}
          >
            <defs>
              {series.map((s, idx) => (
                <linearGradient key={idx} id={`gradient-${idx}`} x1="0" y1="0" x2="0" y2="1">
                  {/* Increased area opacity for stronger shading */}
                  <stop offset="0%" stopColor={s.color} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={s.color} stopOpacity={0.08} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={darkMode ? '#1E3A4E' : '#e5e7eb'}
              vertical={false}
            />
            <XAxis
              dataKey="name"
              tick={<CustomXAxisTick />}
              tickLine={false}
              axisLine={{ stroke: darkMode ? '#1E3A4E' : '#e5e7eb' }}
              interval={xLabels.length > 12 ? Math.floor(xLabels.length / 12) : 0}
              height={40}
            />
            <YAxis
              tickFormatter={(v: number) => formatTickSmart(v, yTicks, currency)}
              tick={{ fontSize: 13, fill: darkMode ? '#9CA3AF' : '#6b7280' }}
              tickLine={false}
              axisLine={false}
              width={axisWidth}
              domain={yDomain}
              ticks={yTicks}
            />
            <Tooltip 
              content={<CustomTooltip />} 
              cursor={<CustomCursor />}
            />
            {series.map((s, idx) => (
              <React.Fragment key={idx}>
                <Area
                  type="linear"
                  dataKey={s.label}
                  fill={`url(#gradient-${idx})`}
                  stroke="none"
                />
                <Line
                  type="linear"
                  dataKey={s.label}
                  stroke={s.color}
                  strokeWidth={3}
                  dot={false}
                  activeDot={{ r: 6, fill: s.color, stroke: '#ffffff', strokeWidth: 3 }}
                />
              </React.Fragment>
            ))}
            {highlight && (
              <ReferenceLine
                x={highlight.x}
                stroke={darkMode ? '#374151' : '#9ca3af'}
                strokeDasharray="3 3"
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};